import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API_URL } from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { CaretLeftIcon, CaretRightIcon, MicrosoftExcelLogoIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { ReciboDetalleModal } from "@/components/ReciboPago";
import { getToken } from "@/lib/token";
import { pedirPaseFactura, conPase } from "@/components/NotaDescarga";

/**
 * Historial del cliente por período (mes, trimestre, semestre, año o rango). Abre en el mes en curso.
 * Totales y saldos calculados en el servidor sobre TODO el período; el detalle se pagina en el servidor.
 * Los filtros viven en la URL: al abrir un documento y volver, se conservan.
 */
const PERIODOS = [["mes", "Mes"], ["trimestre", "Trimestre"], ["semestre", "Semestre"], ["anio", "Año"], ["rango", "Rango personalizado"]];

function moverRef(tipo, ref, delta) {
  const d = new Date(`${ref}T12:00:00`);
  const meses = { mes: 1, trimestre: 3, semestre: 6, anio: 12 }[tipo] || 1;
  d.setMonth(d.getMonth() + delta * meses);
  return d.toISOString().slice(0, 10);
}

const DOC_URL = {
  factura: (id) => `/invoices/${id}/pdf`, pago: (id) => `/payments/${id}/receipt-pdf`,
  nota_credito: (id) => `/credit-notes/${id}/pdf`, nota_debito: (id) => `/debit-notes/${id}/pdf`,
};

async function abrirDocumento(doc) {
  const f = DOC_URL[doc?.tipo];
  if (!f) return;
  let pase = "";
  if (doc.tipo === "factura") {
    pase = await pedirPaseFactura({ id: doc.id, numero: doc.numero || doc.referencia || "" });   // RTC: nota de descarga
    if (pase === null) return;
  }
  try {
    const res = await fetch(`${API_URL}${conPase(f(doc.id), pase)}`, {
      headers: { Authorization: `Bearer ${getToken()}`,
                 "X-Company-Id": localStorage.getItem("agroport_company_id") || "" },
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || "No tienes acceso a este documento");
    }
    window.open(URL.createObjectURL(await res.blob()), "_blank", "noopener");
  } catch (e) { toast.error(e.message); }
}

export default function ClientHistoryTab({ cid, hoy }) {
  const [sp, setSp] = useSearchParams();
  const periodo = sp.get("periodo") || "mes";
  const ref = sp.get("ref") || hoy;
  const desde = sp.get("desde") || "";
  const hasta = sp.get("hasta") || "";
  const page = Number(sp.get("pag") || 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recibo, setRecibo] = useState(null);     // detalle del recibo de un movimiento de pago
  const [rango, setRango] = useState({ desde, hasta });

  const setParams = (patch) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setSp(n, { replace: true });
  };

  const load = useCallback(async () => {
    if (periodo === "rango" && (!desde || !hasta)) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${cid}/historial`, {
        params: { periodo, ref, desde: desde || undefined, hasta: hasta || undefined, page, page_size: 25 } });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); setData(null); }
    finally { setLoading(false); }
  }, [cid, periodo, ref, desde, hasta, page]);
  useEffect(() => { load(); }, [load]);

  const exportar = async () => {
    const q = new URLSearchParams({ periodo, ref, ...(desde ? { desde } : {}), ...(hasta ? { hasta } : {}) });
    try {
      const res = await fetch(`${API_URL}/clients/${cid}/historial/excel?${q}`, {
        headers: { Authorization: `Bearer ${getToken()}`,
                   "X-Company-Id": localStorage.getItem("agroport_company_id") || "" } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "No se pudo exportar");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob());
      a.download = `historial-${data?.cliente?.codigo || "cliente"}-${data?.periodo?.desde}_${data?.periodo?.hasta}.xlsx`;
      a.click();
    } catch (e) { toast.error(e.message); }
  };

  const aplicarRango = () => {
    if (!rango.desde || !rango.hasta) return toast.error("Indica la fecha inicial y la final");
    if (rango.desde > rango.hasta) return toast.error("La fecha inicial no puede ser posterior a la final");
    setParams({ desde: rango.desde, hasta: rango.hasta, pag: "" });
  };

  return (
    <div className="space-y-4" data-testid="client-history-period">
      <div className="bg-card border border-border/60 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">Período</label>
          <select value={periodo} onChange={(e) => setParams({ periodo: e.target.value, pag: "", ...(e.target.value !== "rango" ? { desde: "", hasta: "" } : {}) })}
                  data-testid="hist-periodo" className="bg-app border border-border rounded-md px-3 py-2 text-sm">
            {PERIODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {periodo !== "rango" ? (
          <div className="flex items-center gap-1">
            <button onClick={() => setParams({ ref: moverRef(periodo, ref, -1), pag: "" })} data-testid="hist-anterior"
              className="h-9 px-2 border border-border rounded-md text-sm" aria-label="Período anterior"><CaretLeftIcon size={14} /></button>
            <input type="date" value={ref} onChange={(e) => setParams({ ref: e.target.value, pag: "" })} data-testid="hist-ref"
              className="bg-app border border-border rounded-md px-2 py-1.5 text-sm" title="Cualquier día del período" />
            <button onClick={() => setParams({ ref: moverRef(periodo, ref, 1), pag: "" })} data-testid="hist-siguiente"
              className="h-9 px-2 border border-border rounded-md text-sm" aria-label="Período siguiente"><CaretRightIcon size={14} /></button>
            <button onClick={() => setParams({ ref: hoy, pag: "" })} className="h-9 px-3 border border-border rounded-md text-xs">Actual</button>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <div><label className="text-[10px] uppercase text-text-muted block mb-1">Desde</label>
              <input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} data-testid="hist-desde"
                className="bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></div>
            <div><label className="text-[10px] uppercase text-text-muted block mb-1">Hasta</label>
              <input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} data-testid="hist-hasta"
                className="bg-app border border-border rounded-md px-2 py-1.5 text-sm" /></div>
            <button onClick={aplicarRango} data-testid="hist-aplicar" className="h-9 px-3 bg-agro-green text-black rounded-md text-sm font-medium">Consultar</button>
          </div>
        )}
        <div className="text-xs text-text-secondary" data-testid="hist-etiqueta">{data?.periodo?.etiqueta}</div>
        <button onClick={exportar} disabled={!data} data-testid="hist-excel"
          className="ml-auto h-9 px-3 border border-agro-green/50 text-agro-green rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
          <MicrosoftExcelLogoIcon size={16} /> Exportar historial a Excel
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="hist-resumen">
          {[["Saldo inicial", data.saldo_inicial], ["Cargos", data.cargos], ["Pagos", data.pagos], ["Ajustes", data.ajustes],
            ["Saldo final del período", data.saldo_final]].map(([l, v]) => (
            <div key={l} className="bg-card border border-border/60 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">{l}</div>
              <div className="font-heading text-lg">{fmtL(v)}</div>
            </div>
          ))}
          <div className="bg-agro-green/5 border border-agro-green/30 rounded-lg p-3" title="Saldo real del cliente hoy (no depende del período)">
            <div className="text-[10px] uppercase tracking-wider text-agro-green">Saldo actual del cliente</div>
            <div className="font-heading text-lg text-agro-green" data-testid="hist-saldo-actual">{fmtL(data.saldo_actual)}</div>
          </div>
        </div>
      )}

      <div className="bg-card border border-border/60 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="hist-tabla">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr><th className="p-2.5 text-left">Fecha</th><th className="p-2.5 text-left">Tipo</th><th className="p-2.5 text-left">Número</th>
              <th className="p-2.5 text-left">Concepto</th><th className="p-2.5 text-left">Vence</th><th className="p-2.5 text-left">Usuario</th><th className="p-2.5 text-right">Cargos</th>
              <th className="p-2.5 text-right">Abonos</th><th className="p-2.5 text-right">Saldo</th><th className="p-2.5"></th></tr>
          </thead>
          <tbody>
            {data && page === 1 && (
              <tr className="border-t border-border/40 bg-sidebar/30"><td colSpan={8} className="p-2.5 text-xs text-text-secondary">Saldo inicial del período</td>
                <td className="p-2.5 text-right font-mono">{fmtL(data.saldo_inicial)}</td><td /></tr>
            )}
            {loading && <tr><td colSpan={10} className="p-6 text-center text-text-muted">Cargando…</td></tr>}
            {!loading && data && data.movimientos.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-text-muted" data-testid="hist-vacio">Sin movimientos en el período</td></tr>)}
            {!loading && data?.movimientos.map((m, i) => (
              <tr key={i} className={`border-t border-border/40 ${m.anulado ? "opacity-60" : ""}`} data-testid={`hist-mov-${m.numero}`}>
                <td className="p-2.5 whitespace-nowrap">{fmtDate(m.fecha)}</td>
                <td className="p-2.5">{m.tipo}</td>
                <td className="p-2.5 font-mono text-xs">
                  {m.doc?.tipo === "pago" ? (
                    <button onClick={() => setRecibo(m.doc.id)} className="text-agro-green hover:underline" title="Ver recibo y su aplicación a facturas"
                      data-testid={`hist-recibo-${m.numero}`}>{m.numero}</button>
                  ) : m.numero}
                </td>
                <td className="p-2.5 text-xs text-text-secondary">{m.concepto}</td>
                <td className="p-2.5 text-xs">{m.vencimiento ? fmtDate(m.vencimiento) : "—"}</td>
                <td className="p-2.5 text-xs text-text-secondary">{m.usuario || ""}</td>
                <td className="p-2.5 text-right font-mono">{m.cargo ? fmtL(m.cargo) : ""}</td>
                <td className="p-2.5 text-right font-mono">{m.abono ? fmtL(m.abono) : ""}</td>
                <td className="p-2.5 text-right font-mono">{fmtL(m.saldo)}</td>
                <td className="p-2.5 text-right">
                  {DOC_URL[m.doc?.tipo] && m.tipo !== "Ajuste de saldo" && (
                    <button onClick={() => abrirDocumento(m.doc)} title="Ver documento" aria-label="Ver documento" className="text-text-muted hover:text-agro-green">
                      <ArrowSquareOutIcon size={15} /></button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.paginas > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-border/40 text-xs" data-testid="hist-paginacion">
            <span className="text-text-muted">{data.total_movimientos} movimientos · página {data.page} de {data.paginas}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setParams({ pag: String(page - 1) })} className="px-3 py-1.5 border border-border rounded disabled:opacity-40">Anterior</button>
              <button disabled={page >= data.paginas} onClick={() => setParams({ pag: String(page + 1) })} className="px-3 py-1.5 border border-border rounded disabled:opacity-40">Siguiente</button>
            </div>
          </div>
        )}
      </div>
      {recibo && <ReciboDetalleModal pagoId={recibo} onClose={() => setRecibo(null)} />}
    </div>
  );
}
